import { useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, CircularProgress, Dialog, DialogTitle, Grid, Typography } from '@mui/material'
import { GridColDef, GridRowParams } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'

import { SearchBar } from 'app/lib/components/SearchBar'
import { CursorPaginatedTable } from 'app/lib/components/table'
import { EligibilityRecordStatus } from 'app/models/scribe.models'
import { useGetEligibilityRecordsQuery } from 'app/redux/scribeApi'
import { Manage, NoSearchResults, Refresh } from 'assets/images'

import { SubmittedMember } from './RequestForm'

const MEMBERS_PER_PAGE = 5

const style = {
  info: { maxWidth: '50%', margin: '0 auto 2em', textAlign: 'center' },
  memberBox: { margin: 4, boxShadow: '0px 2px 4px rgba(188, 188, 188, 0.5)' },
  loading: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(55, 55, 54, 0.6)',
  },
}

type MemberSearchModalProps = Readonly<{
  open: boolean
  title: string
  organizationId: string
  planIds: number[]
  onClose: () => void
  onSelectMember: (member: SubmittedMember) => void
}>

export const MemberSearchModal = ({
  open,
  title,
  organizationId,
  planIds,
  onClose,
  onSelectMember,
}: MemberSearchModalProps) => {
  const { t } = useTranslation()

  const [search, setSearch] = useState('')
  const [cursor, setCursor] = useState<string | undefined>()
  const [isPaging, setIsPaging] = useState(false)

  const { data, currentData, isFetching, isError } = useGetEligibilityRecordsQuery(
    {
      search,
      cursor,
      limit: MEMBERS_PER_PAGE,
      organizationId: [Number(organizationId)],
      status: EligibilityRecordStatus.ACTIVE,
      plan_id: planIds,
    },
    { skip: !search, refetchOnMountOrArgChange: true },
  )

  const displayedPage = isPaging ? data : currentData
  const records = displayedPage?.data || []
  const isSearching = isFetching && !isPaging
  const showErrorState = isError
  const showMembers = !isError && !!search && !isSearching && records.length > 0
  const showEmptyState = !isError && !!search && !isFetching && records.length === 0
  const showInitialState = !showMembers && !showEmptyState && !showErrorState

  const handleSearch = (searchValue: string) => {
    setIsPaging(false)
    setCursor(undefined)
    setSearch(searchValue)
  }

  const handleFetchData = (nextCursor?: string) => {
    setIsPaging(true)
    setCursor(nextCursor)
  }

  const handleExited = () => {
    setIsPaging(false)
    setSearch('')
    setCursor(undefined)
  }

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      flex: 1,
      renderCell: (params) => <Typography variant="subtitle2">{params.value}</Typography>,
    },
    {
      field: 'lastName',
      flex: 1,
      renderCell: (params) => <Typography variant="subtitle2">{params.value}</Typography>,
    },
    {
      field: 'uniqueIdentifier',
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" title={params.value}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'select',
      flex: 0.5,
      align: 'right',
      renderCell: () => (
        <Typography variant="subtitle2" sx={{ color: 'accent.main' }}>
          {t('searchMember.modal.select')}
        </Typography>
      ),
    },
  ]

  const rows = records.map(({ id, attributes }) => ({
    id,
    firstName: attributes.firstName,
    lastName: attributes.lastName,
    uniqueIdentifier: attributes.uniqueIdentifier,
  }))

  const handleRowClick = ({ row }: GridRowParams) =>
    onSelectMember({ id: row.id, name: `${row.firstName} ${row.lastName}` })

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={onClose}
      TransitionProps={{ onExited: handleExited }}
      data-testid="member-modal"
    >
      <Box sx={{ padding: 2.5 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <DialogTitle>{title}</DialogTitle>
          </Grid>
          <Grid item>
            <Button variant="text" onClick={onClose}>
              <CloseIcon fontSize="small" sx={{ color: 'primary.main' }} />
            </Button>
          </Grid>
        </Grid>
        <Box sx={{ maxWidth: '512px', marginLeft: 3 }}>
          <SearchBar
            inputLabel={t('searchMember.modal.search')}
            onSearch={handleSearch}
            autofocus
          />
        </Box>
        {showInitialState && (
          <Grid container direction="column" alignItems="center" sx={style.info}>
            <Manage />
            <Box sx={{ marginTop: -1.5, marginBottom: 0.5 }}>
              <Typography variant="h3">{t('searchMember.modal.initial.subtitle')}</Typography>
            </Box>
            <Typography variant="body1">{t('searchMember.modal.initial.copy')}</Typography>
          </Grid>
        )}
        {showEmptyState && (
          <Grid
            container
            direction="column"
            alignItems="center"
            sx={style.info}
            data-testid="no-results"
          >
            <NoSearchResults />
            <Box sx={{ marginTop: -1.5, marginBottom: 0.5 }}>
              <Typography variant="h3">{t('searchMember.modal.empty.subtitle')}</Typography>
            </Box>
            <Typography variant="body1">{t('searchMember.modal.empty.copy')}</Typography>
          </Grid>
        )}
        {showMembers && (
          <Box sx={style.memberBox}>
            <CursorPaginatedTable
              key={search}
              rows={rows}
              columns={columns}
              limit={MEMBERS_PER_PAGE}
              pagination={{
                nextCursor: displayedPage?.meta.nextCursor,
                prevCursor: displayedPage?.meta.prevCursor,
              }}
              onFetchData={handleFetchData}
              onRowClick={handleRowClick}
              hideColumnHeaders
              testID="members-table"
            />
          </Box>
        )}
        {showErrorState && (
          <Grid
            container
            direction="column"
            alignItems="center"
            sx={style.info}
            data-testid="error-state"
          >
            <Refresh />
            <Box sx={{ marginTop: -1.5, marginBottom: 0.5 }}>
              <Typography variant="h3">{t('searchMember.modal.error.subtitle')}</Typography>
            </Box>
            <Typography variant="body1">{t('searchMember.modal.error.copy')}</Typography>
          </Grid>
        )}
      </Box>
      {isFetching && (
        <Grid container sx={style.loading} alignItems="center" justifyContent="space-around">
          <CircularProgress size={150} />
        </Grid>
      )}
    </Dialog>
  )
}
